import { useMutation } from "@tanstack/react-query";
import { signOut } from "next-auth/react";
import { UsersApi } from "@/openapi/generated";
import type { Result } from "@/openapi/generated/models";
import { useAccessToken } from "@/hooks";
import type { ChangePasswordFormType } from "@/schemas";
import { buildLoginUrlAfterPasswordChange } from "@/lib/utils/auth/passwordChangedRedirect";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";

function getChangePasswordErrorMessage(result: Result): string {
  const message = result.error?.message?.trim();
  return message || "Failed to update password";
}

export const useChangePassword = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({
      email,
      formData,
    }: {
      email: string;
      formData: ChangePasswordFormType;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(UsersApi, accessToken);
      const result = await service.apiUserAuthChangePasswordPost({
        changePasswordRequest: {
          email,
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        },
      });

      if (!result.isSuccess) {
        throw new Error(getChangePasswordErrorMessage(result));
      }

      return result;
    },
    onSuccess: async () => {
      await signOut({
        redirect: true,
        callbackUrl: buildLoginUrlAfterPasswordChange(),
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to update password",
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isSuccess, isError };
};
